import type {ResolverType, ApprovalStrategy} from './approval-policy'
import type {ApprovalStepStatus} from './access-request'
import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import type {EscalationLevel} from './escalation'
import type {InitialOwnerRequest} from './owner'
/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

export const RestrictionRuleRequestStatus = {
    DRAFT: 'DRAFT',
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    CANCELLED: 'CANCELLED',
} as const
export type RestrictionRuleRequestStatus = typeof RestrictionRuleRequestStatus[keyof typeof RestrictionRuleRequestStatus]

export const RestrictionRuleRequestType = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    ACTIVATE: 'ACTIVATE',
    DEACTIVATE: 'DEACTIVATE',
} as const
export type RestrictionRuleRequestType = typeof RestrictionRuleRequestType[keyof typeof RestrictionRuleRequestType]

export interface RestrictionRuleRequestApprovalStepApprover {
    id: UUID
    approver: EntityLink
    assignedAt: Instant
}

export interface RestrictionRuleRequestApprovalStepApprovalDecision {
    id: UUID
    approver: EntityLink
    decision: string
    comment: string | null
    decidedAt: Instant
}

export interface RestrictionRuleRequestApprovalStep {
    id: UUID
    stepOrder: number
    resolverType: ResolverType
    resolverParam: string | null
    approvalStrategy: ApprovalStrategy
    required: boolean
    status: ApprovalStepStatus
    activatedAt: Instant | null
    completedAt: Instant | null
    escalationLevel: EscalationLevel
    lastEscalationAt: Instant | null
    approvers: RestrictionRuleRequestApprovalStepApprover[]
    decisions: RestrictionRuleRequestApprovalStepApprovalDecision[]
}

export interface RestrictionRuleRequestOwnerResponse {
    ownerPartyId: UUID
    ownerPartyName: string
    responsibility: string
}

export interface RestrictionRuleRequest {
    id: UUID
    ruleId: UUID | null
    linkedRuleName: string | null
    linkedRuleStatus: string | null
    requestType: RestrictionRuleRequestType
    status: RestrictionRuleRequestStatus
    name: string
    description: string | null
    conditionExpr: string | null
    deniedProfileIds: UUID[]
    deniedProfileCondition: string | null
    removalGraceDays: number
    owners: RestrictionRuleRequestOwnerResponse[]
    matchedPolicyId: UUID | null
    matchedPolicyName: string | null
    reviewedAt: Instant | null
    reviewedBy: EntityLink | null
    reviewComment: string | null
    version: number
    createdAt: Instant
    updatedAt: Instant
    approvalSteps: RestrictionRuleRequestApprovalStep[]
}

export const RestrictionRuleRequestCreateRequestFields = {
    requestType: 'requestType',
    ruleId: 'ruleId',
    name: 'name',
    description: 'description',
    conditionExpr: 'conditionExpr',
    deniedProfileIds: 'deniedProfileIds',
    deniedProfileCondition: 'deniedProfileCondition',
    removalGraceDays: 'removalGraceDays',
    owners: 'owners',
} as const

export interface RestrictionRuleRequestCreateRequest {
    requestType: RestrictionRuleRequestType
    ruleId?: UUID
    name: string
    description?: string
    conditionExpr?: string
    deniedProfileIds?: UUID[]
    deniedProfileCondition?: string
    removalGraceDays: number
    owners: InitialOwnerRequest[]
}

export interface RestrictionRuleRequestUpdateRequest {
    name: string
    description?: string
    conditionExpr?: string
    deniedProfileIds?: UUID[]
    deniedProfileCondition?: string
    removalGraceDays: number
    owners: InitialOwnerRequest[]
    version: number
}

/**
 * Request to approve or reject a restriction rule request.
 * Mirrors backend record sodrequest.ReviewRequest.
 */
export interface ReviewRequest {
    comment?: string
}

export interface RestrictionRuleRequestPage {
    content: RestrictionRuleRequest[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export const SodValidateExpressionRequestFields = {
    conditionExpr: 'conditionExpr',
} as const

export interface SodValidateExpressionRequest {
    conditionExpr: string
}

export interface SodParseError {
    line: number
    column: number
    message: string
}

export interface SodValidateExpression {
    valid: boolean
    errors: SodParseError[]
}
